// File: src/features/jobs/useMcpNotificationCenter.ts
// MCP Notification Center Hook
//
// ✅ CENTRALIZES: MCP notification logic across components
// ✅ PROVIDES: Toast-like notification system for MCP events
// ✅ ENABLES: Cross-component MCP result broadcasting

import { useState, useCallback } from 'react';

export interface McpNotification {
  id: string;
  message: string;
  type: 'success' | 'warning' | 'error';
  timestamp: number;
  autoHide?: boolean;
}

interface UseMcpNotificationCenterResult {
  notifications: McpNotification[];
  pushNotification: (message: string, type: McpNotification['type'], autoHide?: boolean) => string;
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
  hasNotificationType: (type: McpNotification['type']) => boolean;
}

export function useMcpNotificationCenter(): UseMcpNotificationCenterResult {
  const [notifications, setNotifications] = useState<McpNotification[]>([]);

  const pushNotification = useCallback((
    message: string, 
    type: McpNotification['type'], 
    autoHide: boolean = true
  ): string => {
    const id = `mcp-notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const notification: McpNotification = {
      id,
      message,
      type,
      timestamp: Date.now(),
      autoHide
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-dismiss success notifications after 5 seconds
    if (autoHide && type === 'success') {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, 5000);
    }

    return id;
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const hasNotificationType = useCallback((type: McpNotification['type']): boolean => {
    return notifications.some(n => n.type === type);
  }, [notifications]);

  return {
    notifications,
    pushNotification,
    dismissNotification,
    clearAllNotifications,
    hasNotificationType
  };
}

/**
 * Helper hook for MCP error deduplication
 * Prevents duplicate error notifications from being pushed
 */
export function useMcpErrorDeduplication() {
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null);
  
  const shouldPushError = useCallback((errorMessage: string | null): boolean => {
    if (!errorMessage) return false;
    if (errorMessage === lastErrorMessage) return false;
    
    setLastErrorMessage(errorMessage);
    return true;
  }, [lastErrorMessage]);

  const clearLastError = useCallback(() => {
    setLastErrorMessage(null);
  }, []);

  return { shouldPushError, clearLastError };
}
